import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { ColumnId, ValueKey } from '../lib/columns'
import type { CellMove, CommitValue } from '../lib/editing'
import type { BudgetAffordances, EditableColumn, EditingCell } from '../lib/permissions'
import type { BudgetRow, CostTree } from '../lib/tree'
import type { CostBuildingRef, CostValues } from '../types'
import BudgetGridFooter from './BudgetGridFooter'
import BudgetGridHeader from './BudgetGridHeader'
import BudgetGridRow from './BudgetGridRow'
import type { RowAction } from './RowMenu'

const ROW_H = 32 // h-8
const HEADER_H = 40 // h-10
const FOOTER_H = 36 // h-9

interface Props {
  rows: BudgetRow[]
  columns: ColumnId[]
  hasInvestorContract: boolean
  visibility: Record<ColumnId, boolean>
  currency: string
  locale: string
  footerLabel: string
  footerValues: CostValues
  affordances: BudgetAffordances
  visible: ReadonlySet<ColumnId>
  tree: CostTree
  buildings: CostBuildingRef[]
  editing: EditingCell | null
  onToggle: (id: string) => void
  onToggleColumn: (id: ColumnId) => void
  onCollapseAll: () => void
  onExpandAll: () => void
  onOpenCell?: (nodeId: string, key: ValueKey) => void
  onStartEdit: (nodeId: string, column: EditableColumn) => void
  onCommit: (nodeId: string, value: CommitValue, move: CellMove) => void
  onCancelEdit: () => void
  onRowAction?: (nodeId: string, action: RowAction) => void
}

/** One scroll container: sticky header, virtualized rows (fixed 32 px), sticky footer; the name column is sticky left in every row. */
const BudgetTreeGrid: React.FC<Props> = ({ rows, columns, hasInvestorContract, visibility, currency, locale, footerLabel, footerValues, affordances, visible, tree, buildings, editing, onToggle, onToggleColumn, onCollapseAll, onExpandAll, onOpenCell, onStartEdit, onCommit, onCancelEdit, onRowAction }) => {
  const { t } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
    scrollMargin: HEADER_H,
    scrollPaddingStart: HEADER_H,
    scrollPaddingEnd: FOOTER_H,
  })
  // The editor may move to a row outside the rendered window (Enter / Tab); bring it in so it can mount and take focus.
  const editingIndex = editing ? rows.findIndex((r) => r.node.activity.id === editing.nodeId) : -1
  useEffect(() => {
    if (editingIndex >= 0) virtualizer.scrollToIndex(editingIndex, { align: 'auto' })
  }, [editingIndex, virtualizer])
  // The scroll-padding classes keep a focused editor clear of the sticky header, footer and name column.
  // Styled scrollbars take layout space: a macOS overlay scrollbar would paint over the sticky footer and the last column.
  // ponytail: WebKit/Blink only, Firefox on macOS still overlays; move the footer out of the scroller if that matters.
  return (
    <div ref={scrollerRef} role="treegrid" aria-label={t('CostControl_tab_budget')} aria-rowcount={rows.length} className="relative h-[calc(100vh-19rem)] min-h-[20rem] scroll-pt-10 scroll-pb-9 scroll-pl-[22rem] overflow-auto rounded-md border [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar]:size-2.5">
      <BudgetGridHeader columns={columns} hasInvestorContract={hasInvestorContract} visibility={visibility} onToggleColumn={onToggleColumn} onCollapseAll={onCollapseAll} onExpandAll={onExpandAll} />
      <div
        className="relative min-w-max"
        // ui-drift: dynamic
        style={{ height: rows.length * ROW_H }}
      >
        {virtualizer.getVirtualItems().map((vi) => {
          const row = rows[vi.index]
          return (
            <BudgetGridRow
              key={row.node.activity.id}
              row={row}
              top={vi.index * ROW_H}
              columns={columns}
              currency={currency}
              locale={locale}
              affordances={affordances}
              visible={visible}
              tree={tree}
              buildings={buildings}
              editing={editing?.nodeId === row.node.activity.id ? editing.column : null}
              onToggle={onToggle}
              onOpenCell={onOpenCell}
              onStartEdit={onStartEdit}
              onCommit={onCommit}
              onCancelEdit={onCancelEdit}
              onRowAction={onRowAction}
            />
          )
        })}
      </div>
      <BudgetGridFooter label={footerLabel} values={footerValues} columns={columns} currency={currency} locale={locale} />
    </div>
  )
}
export default BudgetTreeGrid
